import React, { useState, useRef } from "react";
import axiosClient from "../api/axiosClient";
import { Button, Spinner, Toast, ToastContainer } from "react-bootstrap";
import { Mic, MicOff, Send } from "lucide-react";
import "../styles/CreateTask.css";

const CreateTask = () => {
  const [taskText, setTaskText] = useState("");
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);

  // 🎤 Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorderRef.current.ondataavailable = (e) =>
        audioChunks.current.push(e.data);
      mediaRecorderRef.current.onstop = handleRecordingStop;

      mediaRecorderRef.current.start();
      setRecording(true);
      showToast("🎙️ Recording started — speak your task clearly...", "info");
    } catch (err) {
      console.error("Microphone access error:", err);
      showToast("Microphone access denied.", "danger");
    }
  };

  // 🛑 Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      showToast("Processing your voice...", "info");
    }
  };

  // 🎧 When recording stops, upload to backend
  const handleRecordingStop = async () => {
    const blob = new Blob(audioChunks.current, { type: "audio/wav" });
    const formData = new FormData();
    formData.append("file", blob, "recording.wav");

    try {
      const response = await axiosClient.post("/tasks/transcribe_voice", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const transcript = response.data?.data?.transcript;
      if (transcript) {
        setTaskText(transcript);
        showToast("✅ Voice transcribed successfully!", "success");
      } else {
        showToast("⚠️ Transcription failed.", "warning");
      }
    } catch (err) {
      console.error("Transcription error:", err);
      showToast("Error while transcribing voice.", "danger");
    }
  };

  // 🚀 Submit to AI backend
  const handleSubmit = async () => {
    if (!taskText.trim()) {
      showToast("Please type or record your task first.", "warning");
      return;
    }

    setLoading(true);
    try {
      const res = await axiosClient.post("/tasks/create_from_text", { text: taskText });
      if (res.data?.status === "success") {
        showToast("✅ Task created successfully!", "success");
        setTaskText("");
      } else {
        showToast("⚠️ AI could not create the task.", "warning");
      }
    } catch (err) {
      console.error("AI Task Creation Error:", err);
      showToast("Error creating task. Please try again.", "danger");
    } finally {
      setLoading(false);
    }
  };

  // 🔔 Toast helper
  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  return (
    <div className="smart-task-container">
      <div className="text-center mb-4">
        <h6 className="text-secondary mb-2">
          <span role="img" aria-label="ai">✨</span> AI-Powered Task Creation
        </h6>
        <h2 className="smart-task-title">Smart Task Creator</h2>
        <p className="smart-task-subtitle">
          Type your task or use voice input for hands-free creation
        </p>
      </div>

      <div className="smart-task-box">
        <textarea
          className="smart-task-textarea mb-4"
          rows="6"
          placeholder="Describe your task here or use the mic..."
          value={taskText}
          onChange={(e) => setTaskText(e.target.value)}
        />

        <div className="d-flex justify-content-center gap-3">
          {!recording ? (
            <Button className="smart-task-btn btn-record" onClick={startRecording}>
              <Mic size={18} className="me-2" />
              Start Recording
            </Button>
          ) : (
            <Button className="smart-task-btn btn-stop" onClick={stopRecording}>
              <MicOff size={18} className="me-2" />
              Stop Recording
            </Button>
          )}

          <Button
            className="smart-task-btn btn-submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <Send size={18} className="me-2" />
                Submit Task
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast bg={toast.type} show={toast.show} onClose={() => setToast({ show: false })}>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default CreateTask;
